/**
 * PowerOutageLayer Component
 *
 * Displays power outages from Ausgrid on the map.
 *
 * Ausgrid rebuilt their site in 2026 and retired the old Outage.asmx feed; the
 * backend /power/outages proxy now normalizes their replacement outages-map
 * feed into the stable schema below. Each outage is a point (marker) rather
 * than a polygon.
 *
 * Note: Only Ausgrid (Sydney, Central Coast, Hunter) has a usable API.
 * Endeavour Energy (Western Sydney) and Essential Energy (Regional NSW)
 * do not publish comparable outage feeds.
 */

import { useEffect, useState, useMemo, useCallback, Fragment } from 'react';
import { Polygon, Popup, useMap, Marker } from 'react-leaflet';
import L from 'leaflet';

/**
 * Create a lightning bolt icon for power outage markers.
 * Icons are cached per colour (only ~3 distinct colours) so we don't rebuild a
 * DivIcon per outage on every render.
 */
const _iconCache = new Map<string, L.DivIcon>();
function createLightningIcon(color: string): L.DivIcon {
  const cached = _iconCache.get(color);
  if (cached) return cached;
  const icon = _buildLightningIcon(color);
  _iconCache.set(color, icon);
  return icon;
}

function _buildLightningIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: 'power-outage-marker',
    html: `
      <div style="
        width: 28px;
        height: 28px;
        background: ${color};
        border: 2px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      ">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="white">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
        </svg>
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
}

// Read the pre-aggregated outage file from CloudFront (written by the ingestion
// Lambda every ~10 min). This is edge-cached, so it costs nothing per user and
// scales to any number of distributors — unlike hitting the API Lambda, which
// fetched every distributor live on every request. Matches the pattern used by
// the lightning/thunderstorm layers.
const STATIC_BASE_URL = import.meta.env.VITE_STATIC_URL || 'https://d3c2yw8hurho5b.cloudfront.net';
const POWER_OUTAGES_URL = `${STATIC_BASE_URL}/power/outages.json`;

// Refresh interval - 5 minutes
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Normalized power outage as returned by the /power/outages proxy.
 * Mirrors api/power_outages.py::normalize_outage.
 */
// Per-distributor "view details" links for the popup footer.
const SOURCE_LINKS: Record<string, string> = {
  'Ausgrid': 'https://www.ausgrid.com.au/outages',
  'Endeavour Energy': 'https://www.endeavourenergy.com.au/power-outages/outage-map',
  'Essential Energy': 'https://www.essentialenergy.com.au/outages',
  'AusNet': 'https://www.ausnetservices.com.au/outages',
  'CitiPower': 'https://www.citipower.com.au/outages-faults',
  'Powercor': 'https://www.powercor.com.au/outages-faults',
  'United Energy': 'https://www.unitedenergy.com.au/outage-map',
  'Jemena': 'https://poweroutages.jemena.com.au/',
  'Horizon Power': 'https://www.horizonpower.com.au/faults-outages/',
  'Evoenergy': 'https://www.evoenergy.com.au/outages',
  'Energex / Ergon': 'https://www.energex.com.au/outages/outage-finder',
  'SA Power Networks': 'https://www.sapowernetworks.com.au/outages/',
  'Western Power': 'https://www.westernpower.com.au/faults-outages/power-outages',
  'TasNetworks': 'https://www.tasnetworks.com.au/outages',
};

interface PowerOutage {
  id: number | string;
  source: string; // distributor name, e.g. "Ausgrid" / "Western Power"
  displayType: string; // "U" unplanned, "P" planned
  planned: boolean;
  status: string;
  statusLabel: string;
  active: boolean;
  cause: string;
  reason: string | null;
  areas: string[];
  streets: string[];
  customers: number | null;
  startTime: string | null;
  estRestoreTime: string | null;
  actualRestoreTime: string | null;
  updatedTime: string | null;
  delayHours: number | null;
  lat: number;
  lng: number;
  fireRelated: boolean;
  // Affected-area rings ([[lat,lng],...]). Empty for point-only outages.
  polygons: [number, number][][];
}

interface PowerOutageLayerProps {
  enabled?: boolean;
}

/**
 * Get marker color for an outage. Fire-related outages are called out in red;
 * planned maintenance is blue; other unplanned outages are orange.
 */
function getOutageColor(outage: PowerOutage): string {
  if (outage.fireRelated) return '#dc2626'; // Red for fire-related
  if (outage.planned) return '#2563eb'; // Blue for planned maintenance
  return '#f97316'; // Orange for unplanned
}

/**
 * Format an ISO-8601 timestamp from the Ausgrid feed for display.
 */
function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return 'Unknown';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateStr;
  }
}

export default function PowerOutageLayer({ enabled = false }: PowerOutageLayerProps) {
  const map = useMap();
  const [outages, setOutages] = useState<PowerOutage[]>([]);
  // Loading/error are captured for future surfacing (toast or banner). For
  // now they are write-only — the setters still report state changes for a
  // future failure UI to read via context/devtools.
  const [, setIsLoading] = useState(false);
  const [, setError] = useState<string | null>(null);
  const [viewBounds, setViewBounds] = useState<L.LatLngBounds | null>(null);

  /**
   * Fetch outages from our API proxy (which fetches from Ausgrid).
   */
  const fetchOutages = useCallback(async () => {
    if (!enabled) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(POWER_OUTAGES_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch outages: ${response.status}`);
      }

      const data = await response.json();
      const outageList: PowerOutage[] = data.outages || [];
      setOutages(outageList);

      console.log(`[PowerOutageLayer] Loaded ${data.count} outages from Ausgrid${data.fire_related_count > 0 ? ` (${data.fire_related_count} fire-related)` : ''}`);
    } catch (err) {
      console.error('[PowerOutageLayer] Error fetching outages:', err);
      setError(err instanceof Error ? err.message : 'Failed to load power outages');
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  // Fetch outages on mount and when enabled changes
  useEffect(() => {
    if (enabled) {
      fetchOutages();
    } else {
      setOutages([]);
    }
  }, [enabled, fetchOutages]);

  // Set up auto-refresh
  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(fetchOutages, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, fetchOutages]);

  // Track map bounds for viewport filtering. 150ms debounce so a flick-pan
  // (which emits several moveend events as inertia settles) doesn't refilter
  // each time. Matches the existing pattern in HotspotLayer.
  useEffect(() => {
    if (!map) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const updateBounds = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setViewBounds(map.getBounds());
      }, 150);
    };
    updateBounds();
    map.on('moveend', updateBounds);
    map.on('zoomend', updateBounds);
    return () => {
      if (timer) clearTimeout(timer);
      map.off('moveend', updateBounds);
      map.off('zoomend', updateBounds);
    };
  }, [map]);

  // Filter outages to viewport with buffer
  const visibleOutages = useMemo(() => {
    if (!outages.length || !viewBounds) return outages;

    // Add buffer for smooth panning
    const latSpan = viewBounds.getNorth() - viewBounds.getSouth();
    const lngSpan = viewBounds.getEast() - viewBounds.getWest();
    const latBuffer = latSpan * 0.2;
    const lngBuffer = lngSpan * 0.2;

    const bufferedBounds = L.latLngBounds(
      [viewBounds.getSouth() - latBuffer, viewBounds.getWest() - lngBuffer],
      [viewBounds.getNorth() + latBuffer, viewBounds.getEast() + lngBuffer]
    );

    return outages.filter(outage =>
      bufferedBounds.contains([outage.lat, outage.lng])
    );
  }, [outages, viewBounds]);

  if (!enabled) return null;

  // Shared popup for an outage — attached to both the affected-area polygon and
  // the marker icon, so clicking either opens the modal.
  const renderPopup = (outage: PowerOutage, color: string, areaLabel: string) => (
    <Popup className="power-outage-popup">
              <div className="power-outage-popup-content">
                <div className="power-outage-popup-header">
                  <span className="power-outage-popup-icon">⚡</span>
                  <span className="power-outage-popup-title">
                    {outage.planned ? 'Planned Outage' : 'Power Outage'}
                  </span>
                </div>

                <div className="power-outage-popup-details">
                  <div className="power-outage-popup-row">
                    <span className="power-outage-popup-label">Area:</span>
                    <span className="power-outage-popup-value">{areaLabel}</span>
                  </div>

                  <div className="power-outage-popup-row">
                    <span className="power-outage-popup-label">Cause:</span>
                    <span className="power-outage-popup-value" style={{ color }}>
                      {outage.cause || 'Under Investigation'}
                    </span>
                  </div>

                  <div className="power-outage-popup-row">
                    <span className="power-outage-popup-label">Customers:</span>
                    <span className="power-outage-popup-value">
                      {outage.customers?.toLocaleString() || 'Unknown'}
                    </span>
                  </div>

                  <div className="power-outage-popup-row">
                    <span className="power-outage-popup-label">Started:</span>
                    <span className="power-outage-popup-value">
                      {formatDateTime(outage.startTime)}
                    </span>
                  </div>

                  {outage.estRestoreTime && (
                    <div className="power-outage-popup-row">
                      <span className="power-outage-popup-label">Est. Restore:</span>
                      <span className="power-outage-popup-value">
                        {formatDateTime(outage.estRestoreTime)}
                      </span>
                    </div>
                  )}

                  <div className="power-outage-popup-row">
                    <span className="power-outage-popup-label">Status:</span>
                    <span className="power-outage-popup-value">
                      {outage.statusLabel || 'Active'}
                    </span>
                  </div>
                </div>

                <div className="power-outage-popup-footer">
                  <span className="power-outage-popup-source">Source: {outage.source}</span>
                  <a
                    href={SOURCE_LINKS[outage.source] || 'https://www.ausgrid.com.au/outages'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="power-outage-popup-link"
                  >
                    View Details
                  </a>
                </div>
              </div>
    </Popup>
  );

  return (
    <>
      {visibleOutages.map((outage) => {
        const color = getOutageColor(outage);
        const icon = createLightningIcon(color);
        const areaLabel = outage.areas.length > 0 ? outage.areas.join(', ') : 'Unknown';
        const fillOpacity = outage.fireRelated ? 0.4 : 0.25;

        return (
          <Fragment key={outage.id}>
            {outage.polygons.map((ring, i) => (
              <Polygon
                key={`poly-${i}`}
                positions={ring}
                pathOptions={{ color, weight: 2, opacity: 0.8, fillColor: color, fillOpacity }}
              >
                {renderPopup(outage, color, areaLabel)}
              </Polygon>
            ))}
            <Marker position={[outage.lat, outage.lng]} icon={icon}>
              {renderPopup(outage, color, areaLabel)}
            </Marker>
          </Fragment>
        );
      })}
    </>
  );
}
